import { Crown, Check, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { NOTCH } from "@/constants/testIds";

const FREE = [
  { label: "Up to 7 meal plans (lifetime)", on: true },
  { label: "1-day plans", on: true },
  { label: "Plan history & dashboard", on: true },
  { label: "3-day & 7-day plans", on: false },
  { label: "Share plan link", on: false },
  { label: "Photo log with AI macros", on: false },
];

const PRO = [
  { label: "60 meal plans per month", on: true },
  { label: "1-day, 3-day & 7-day plans", on: true },
  { label: "Share plan link (Notch members)", on: true },
  { label: "30 photo-log analyses per month", on: true },
  { label: "Plan history & dashboard", on: true },
  { label: "Priority on all new features", on: true },
];

export default function Pricing() {
  const { billing } = useAuth();
  const isPro = billing?.subscription_status === "active";

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-8 pt-8 sm:pt-10 pb-24">
      <div className="text-center mb-10 sm:mb-12">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C06F52] mb-3">
          <span className="h-px w-8 bg-[#C06F52]" />Plans
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C4C3B] leading-tight">A pocket nutritionist,<br/>for the cost of a chai a week.</h1>
        <p className="mt-4 text-[#666] max-w-md mx-auto">
          Try the first 7 plans free. Upgrade when you're ready for the full week.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
        {/* FREE */}
        <article className="rounded-[1.75rem] bg-white border border-[#E5E2DC] p-7 sm:p-9 flex flex-col">
          <div className="flex items-baseline justify-between gap-6 mb-2">
            <h2 className="font-serif-display text-2xl text-[#222]">Free</h2>
            <div className="font-serif-display text-3xl text-[#222] leading-none">₹0</div>
          </div>
          <p className="text-xs text-[#888] mb-6">Try Notch with no commitment.</p>
          <ul className="space-y-2.5 mb-7 flex-1">
            {FREE.map((p, i) => (
              <li key={i} className={`flex items-start gap-2.5 text-sm ${p.on ? "text-[#333]" : "text-[#aaa] line-through decoration-[#ccc]"}`}>
                <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${p.on ? "bg-[#2C4C3B]/10" : "bg-[#f0eee9]"}`}>
                  {p.on ? <Check size={11} className="text-[#2C4C3B]" /> : <X size={11} className="text-[#aaa]" />}
                </span>
                <span>{p.label}</span>
              </li>
            ))}
          </ul>
          {!isPro && billing && (
            <p className="text-center text-xs text-[#888] mt-auto">
              <span className="text-[#2C4C3B] font-medium">{billing.free_remaining}</span> of {billing.free_limit} free plans left
            </p>
          )}
        </article>

        {/* PRO */}
        <article className="rounded-[1.75rem] bg-[#2C4C3B] text-[#F9F8F6] p-7 sm:p-9 relative overflow-hidden flex flex-col shadow-[0_30px_60px_-30px_rgba(44,76,59,0.45)]">
          <span className="absolute top-5 right-5 inline-flex items-center gap-1 bg-[#C06F52] text-white text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full">
            <Crown size={11} /> Most loved
          </span>
          <div className="flex items-baseline justify-between gap-6 mb-2">
            <h2 className="font-serif-display text-3xl">Notch Pro</h2>
            <div className="text-right">
              <div className="font-serif-display text-4xl leading-none">₹49</div>
              <div className="text-[10px] opacity-70 uppercase tracking-[0.18em] mt-1">/month</div>
            </div>
          </div>
          <p className="text-xs opacity-75 mb-6">Cancel anytime. Generous monthly limits.</p>
          <ul className="space-y-2.5 mb-7 flex-1">
            {PRO.map((p, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-[#F9F8F6]/15 flex items-center justify-center flex-shrink-0">
                  <Check size={11} className="text-[#F9F8F6]" />
                </span>
                <span>{p.label}</span>
              </li>
            ))}
          </ul>

          {isPro ? (
            <div className="text-center py-3.5 rounded-full bg-[#F9F8F6]/15 text-[#F9F8F6] text-sm font-medium inline-flex items-center justify-center gap-2 w-full">
              <Crown size={14} /> You're a Pro. Thanks for supporting Notch.
            </div>
          ) : (
            <>
              <button
                data-testid={NOTCH.upgradeBtn}
                disabled
                aria-disabled="true"
                title="Payments coming soon"
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#F9F8F6]/20 text-[#F9F8F6] text-base font-medium cursor-not-allowed select-none"
              >
                <Crown size={16} /> Upgrade for ₹49/month
              </button>
              <p className="mt-3 text-center text-[11px] text-[#F9F8F6]/70">
                Payments are not live yet — coming soon.
              </p>
            </>
          )}

          {isPro && billing && (
            <div className="mt-5 pt-5 border-t border-white/15 text-[11px] opacity-80 space-y-1">
              <p>Plans this month: <span className="font-medium">{billing.pro_gen_used}/{billing.pro_gen_limit}</span></p>
              <p>Photo analyses: <span className="font-medium">{billing.pro_photo_used}/{billing.pro_photo_limit}</span></p>
              <p className="opacity-70">Resets on the 1st of next month.</p>
            </div>
          )}
        </article>
      </div>

      <p className="mt-8 text-center text-[11px] text-[#999]">
        We're keeping things simple for now — paid plans are coming soon. Until then enjoy the 7 free plans.
      </p>
    </main>
  );
}
